import { useCallback } from 'react';
import { usePermissions, UserPermission, WriteOn, ReadOn } from 'modules/permissions';
import { ConditionalRender } from 'components/layout/ConditionalRender';
import { IssueDisplayEnum } from '../../../enums/issue-display.enum';
import { Issue } from '../../../queries/view-issue.queries.types';
import {
   StyledStatusBarUpdatedBy,
   StyledSubHeader,
   StyledSubHeaderControl,
   StyledSubHeaderControlContainer,
   StyledSubHeaderGroup,
   StyledSubHeaderLabel,
   StyledSubHeaderSection,
   StyledSubHeaderTabContainer
} from 'shared/styled/SubHeader.styled';
import SubHeaderTab from 'components/SubHeaderTab';
import { useForm } from 'modules/forms';
import DateTimeInput from 'components/controls/DateTimeInput';
import { FontIcon } from '@fluentui/react';
import { IssueEntity } from 'shared/types/domain/IssueEntity';
import { DateTime } from 'luxon';

interface IssueSubHeaderProps {
   issue: Issue | undefined;
   displayMode: IssueDisplayEnum;
   formHasErrors: boolean;
   goToOverview: () => void;
   goToCompletionDetails: () => void;
   goToAuditLog: () => void;
}

const IssueSubHeader = ({
   issue,
   displayMode,
   formHasErrors,
   goToOverview,
   goToCompletionDetails,
   goToAuditLog
}: IssueSubHeaderProps) => {
   const { updateFields, formErrors } = useForm<IssueEntity>();
   const { hasPermission } = usePermissions();

   const formatDate = (date?: Date): string => {
      if (!date) return '';

      const dt = DateTime.fromJSDate(date);
      return dt.toLocaleString({...DateTime.DATE_SHORT, weekday: 'short', month: 'short'});
   };

   const lastUpdateText = (): string => {
      if (issue) {
         return `Last Updated On ${formatDate(new Date(issue.lastModified))}`;
      }

      return "";
   };

   const allowShowCompletionDetails = useCallback(() => {
      return issue?.completion != undefined;
   }, [issue]);

   const handleIncidentDateChange = (incidentDate: string | null | undefined) => {
      updateFields({
         incidentDate: { runValidation: true, value: incidentDate }
      });
   };

   return (
      <StyledSubHeader>
         <StyledSubHeaderSection style={{ maxWidth: '25%' }}>
            <StyledSubHeaderGroup>
               <StyledSubHeaderControlContainer>
                  <StyledSubHeaderLabel>{'Incident Date'}</StyledSubHeaderLabel>
                  <StyledSubHeaderControl>
                     <ConditionalRender condition={hasPermission(WriteOn(UserPermission.IncidentDate))}>
                        <DateTimeInput
                           value={issue?.incidentDate!.toString()}
                           onDateTimeSelect={handleIncidentDateChange}
                           disableFuture={true}
                           errorMessage={formErrors?.incidentDate}
                        />
                     </ConditionalRender>
                     <ConditionalRender condition={!hasPermission(WriteOn(UserPermission.IncidentDate))}>
                        {issue?.incidentDate.toString()}
                     </ConditionalRender>
                  </StyledSubHeaderControl>
               </StyledSubHeaderControlContainer>
            </StyledSubHeaderGroup>
            <StyledSubHeaderGroup>
               <StyledSubHeaderControlContainer>
                  <StyledSubHeaderLabel>Status</StyledSubHeaderLabel>
                  <StyledSubHeaderControl>
                     {`${issue?.observationStatus.toProperCase()}`}
                  </StyledSubHeaderControl>
               </StyledSubHeaderControlContainer>
            </StyledSubHeaderGroup>
         </StyledSubHeaderSection>

         <StyledSubHeaderSection style={{ flex: '2 1 auto' }}>
            <StyledSubHeaderGroup style={{ justifyContent: 'flex-end' }}>
               <StyledStatusBarUpdatedBy>{lastUpdateText()}</StyledStatusBarUpdatedBy>
            </StyledSubHeaderGroup>

            <StyledSubHeaderGroup style={{ justifyContent: 'flex-end' }}>
               <StyledSubHeaderTabContainer>
                  <SubHeaderTab
                     onClick={goToOverview}
                     isActive={displayMode === IssueDisplayEnum.Overview}
                     display={'Overview'}
                     highlightErrors={formHasErrors}
                  />
                  <ConditionalRender condition={allowShowCompletionDetails}>
                     <SubHeaderTab
                        onClick={goToCompletionDetails}
                        isActive={displayMode === IssueDisplayEnum.Completion}
                        display={'Completion'}
                     />
                  </ConditionalRender>
                  <ConditionalRender condition={hasPermission(ReadOn(UserPermission.Audit))}>
                     <SubHeaderTab
                        onClick={goToAuditLog}
                        isActive={displayMode === IssueDisplayEnum.Audit}
                        display={<FontIcon iconName='History' />}
                        title={'Audit Log'}
                     />
                  </ConditionalRender>
               </StyledSubHeaderTabContainer>
            </StyledSubHeaderGroup>
         </StyledSubHeaderSection>
      </StyledSubHeader>
   );
};

export default IssueSubHeader;
